import { i18n } from '@/utils/i18n';
import type { OtpLoginLabels } from '@vaiu/ui';

function t(key: string, fallback: string): string {
  const value = i18n.__(key);
  return value === key ? fallback : value;
}

export function buildOtpLoginLabels(): OtpLoginLabels {
  return {
    title: t('otp.title', 'Login with OTP'),
    chooseMethodTitle: t('otp.chooseMethodTitle', 'Choose login method'),
    chooseMethodDescription: t(
      'otp.chooseMethodDescription',
      'Select how you would like to receive your verification code'
    ),
    sms: t('otp.sms', 'SMS'),
    email: t('otp.email', 'Email'),
    continueWith: (method) => t('otp.continueWith', 'Continue with {method}').replace('{method}', method),
    enterPhoneTitle: t('otp.enterPhoneTitle', 'Enter your phone number'),
    enterEmailTitle: t('otp.enterEmailTitle', 'Enter your email address'),
    enterPhoneDescription: t(
      'otp.enterPhoneDescription',
      'We will send a verification code to your phone'
    ),
    enterEmailDescription: t(
      'otp.enterEmailDescription',
      'We will send a verification code to your email'
    ),
    phoneLabel: t('otp.phoneLabel', 'Phone number'),
    emailLabel: t('otp.emailLabel', 'Email address'),
    phonePlaceholder: t('otp.phonePlaceholder', '+1234567890'),
    emailPlaceholder: t('otp.emailPlaceholder', 'your@email.com'),
    phoneHint: t('otp.phoneHint', 'Include country code (e.g., +1 for US)'),
    invalidPhone: t(
      'otp.invalidPhone',
      'Please enter a valid phone number with country code (e.g., +1234567890)'
    ),
    invalidEmail: t('otp.invalidEmail', 'Please enter a valid email address'),
    sendCode: t('otp.sendCode', 'Send code'),
    sending: t('otp.sending', 'Sending…'),
    back: t('otp.back', 'Back'),
    verifyTitle: t('otp.verifyTitle', 'Enter verification code'),
    verifyDescription: (masked) =>
      t('otp.verifyDescription', 'Enter the 6-digit code sent to {masked}').replace('{masked}', masked),
    codeExpiresIn: (time) =>
      t('otp.codeExpiresIn', 'Code expires in {time}').replace('{time}', time),
    verifying: t('otp.verifying', 'Verifying…'),
    verify: t('otp.verify', 'Verify'),
    resendPrompt: t('otp.resendPrompt', "Didn't receive the code? Send again"),
    resendWait: (time) =>
      t('otp.resendWait', 'You can request a new code in {time}').replace('{time}', time),
    otpSentTitle: t('otp.otpSentTitle', 'Verification code sent'),
    otpSentDescription: (channel) =>
      t('otp.otpSentDescription', 'Check your {channel} for the code').replace('{channel}', channel),
    loginSuccessTitle: t('otp.loginSuccessTitle', 'Login successful'),
    loginSuccessDescription: t('otp.loginSuccessDescription', 'Welcome back!'),
    resendCooldown: t('otp.resendCooldown', 'Please wait before requesting another code'),
    verifyLocked: (time) =>
      t('otp.verifyLocked', 'Too many failed attempts. Try again in {time}').replace('{time}', time),
    attemptsRemaining: (count) =>
      t('otp.attemptsRemaining', '{count} attempt(s) remaining').replace('{count}', String(count)),
    enterCompleteCode: t('otp.enterCompleteCode', 'Please enter the complete 6-digit code'),
    sendFailed: t('otp.sendFailed', 'Failed to send verification code'),
    verifyFailed: t('otp.verifyFailed', 'Invalid verification code.'),
    accountNotFound: t(
      'otp.accountNotFound',
      'No account found for this phone or email. Sign up first or use password login.'
    ),
    tooManyAttempts: t(
      'otp.tooManyAttempts',
      'Too many failed attempts. Please wait before trying again.'
    ),
    resendBlocked: t('otp.resendBlocked', 'Please wait before requesting another code'),
  };
}
